import React, { ChangeEvent, MutableRefObject, useContext, useEffect, useRef, useState } from 'react'

import MuiSlider from '@mui/material/Slider'
import { useI18n } from '@shopify/react-i18n'
import { Input, Row } from '@vividseats/vivid-ui-kit'
import { inRange } from '@vividseats/vivid-ui-kit/dist/components/slider/component'
import styles from '@vividseats/vivid-ui-kit/dist/components/slider/styles.module.scss'
import classNames from 'classnames'

import filterContext from '@/components/pages/production/components/context'
import type { TSliderValue } from '@/components/pages/production/components/filters/filters-price/price-histogram/component'
import ticketDetailsContext from '@/components/pages/production/components/ticket-details/context'
import ListingsContext from '@/context/listings'
import { trackEvent } from '@/utils/analytics/ga360'

import PriceHistogram from '../price-histogram'

import { TRACKING_TIMEOUT } from './constants'
import { useLowestAndHighestListingsPrice } from './hook'
import priceSliderStyles from './styles.module.scss'

type SliderProps = { singleRow?: boolean }
const Slider: React.FC<SliderProps> = ({ singleRow }) => {
    const [i18n] = useI18n()
    const [showSlider, setShowSlider] = useState(false)

    const {
        state: { priceFilterState },
        dispatch: { onPriceFilterChangeAction: onFilterChange },
    } = useContext(filterContext)
    const {
        state: detailsState,
        dispatch: { toggleDetails },
    } = useContext(ticketDetailsContext)
    const { selectors: listingsSelectors } = useContext(ListingsContext)
    const { globalPriceRangeObject: priceRangeObject, i18nListingsData: i18nData } = listingsSelectors

    const currencySymbol = i18n.getCurrencySymbol(i18nData?.to).symbol || '$'

    const [lowestFilterPrice, highestFilterPrice] = useLowestAndHighestListingsPrice()
    const highestPriceRef = useRef(highestFilterPrice)
    const lowestPriceRef = useRef(lowestFilterPrice)
    const range = [lowestPriceRef.current, highestPriceRef.current]

    useEffect(() => {
        highestPriceRef.current = highestFilterPrice
        lowestPriceRef.current = lowestFilterPrice
    }, [highestFilterPrice, lowestFilterPrice])

    const [displayedMinMaxValue, setDisplayedMinMaxValue] = useState<TSliderValue>([
        lowestPriceRef.current,
        highestPriceRef.current,
    ])
    useEffect(() => {
        /**
         * This is to decouple our display values from our context state values used for
         * filtering. Without this decoupling the user can never completely delete an input's
         * value due to logic in the `onChange` handler which requires the input value to always
         * be a number.
         *
         * We use +/- infinity so we can capture the entire range of all possible values
         * without having a value that a user *could* manually enter such as MAX_SAFE_INTEGER.
         * The reason the infinities seem backwards is because in the filter context's
         * `filteredListingsSelectorMap` we check if the min is *larger* and
         * that the max is *smaller* than their lowest and highest prices respectively.
         * This approach allows the list to be filtered as if the currentMin/max are as inclusive
         * as possible. It's super weird but if we don't do it this way then our filtered list
         * won't update if a user clears out their values entered into the input.
         */
        const { currentMin, currentMax } = priceFilterState
        const minDisplayValue = currentMin === Number.POSITIVE_INFINITY ? 0 : currentMin
        const maxDisplayValue = currentMax === Number.NEGATIVE_INFINITY ? 0 : currentMax
        setDisplayedMinMaxValue([minDisplayValue, maxDisplayValue])

        setTimeout(() => setShowSlider(true), 250)
    }, [priceFilterState.currentMin, priceFilterState.currentMax, priceFilterState])

    const onLowerBlur = (e: ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value) || 0
        const currentMax = priceFilterState.currentMax
        const newMin = !inRange(value, range) ? lowestPriceRef.current : value

        onFilterChange({ field: 'currentMin', value: newMin, shouldTrackFilterChange: true })

        if (newMin > currentMax) {
            onFilterChange({ field: 'currentMax', value: newMin, shouldTrackFilterChange: true })
        }
    }

    const onUpperBlur = (e: ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(stripPlus(e.target.value))
        const currentMin = priceFilterState.currentMin
        // Ensure that the value entered is neither lower than the current min nor higher than the highest possible price and is not blank
        const newMax = isNaN(value)
            ? highestPriceRef.current
            : Math.min(highestPriceRef.current, Math.max(value, currentMin))
        onFilterChange({ field: 'currentMax', value: newMax, shouldTrackFilterChange: true })
    }

    const stripPlus = (value: string) => {
        if (value.at(-1) === '+') {
            return value.slice(0, -1)
        }
        return value
    }

    const sliderRow = classNames(priceSliderStyles.input, {
        [priceSliderStyles.singleRow]: singleRow,
        [styles.inputRow]: !singleRow,
    })

    const currencyInput = classNames(styles.currencyInput, {
        [priceSliderStyles.currencyInput]: singleRow,
    })

    const inputStyle = classNames(styles.input, {
        [priceSliderStyles.inputPadding]: currencySymbol.length > 1,
    })

    const iconStyle = classNames(styles.icon, {
        [priceSliderStyles.iconPosition]: currencySymbol.length > 1,
    })

    const timeoutRef: MutableRefObject<number | null> = useRef(null)

    const sliderElement = (
        <div className={priceSliderStyles.priceRangeFilterContainer}>
            <PriceHistogram displayMinMaxVal={displayedMinMaxValue} className={priceSliderStyles.priceHistogram} />
            <MuiSlider
                color="secondary"
                className={priceSliderStyles.slider}
                value={displayedMinMaxValue}
                valueLabelDisplay="off"
                min={lowestFilterPrice}
                max={highestFilterPrice}
                getAriaLabel={(id) => `${id ? 'Higher' : 'Lower'}-thumb`} // id 0 -> lower, 1 -> higher
                getAriaValueText={(state) => `Thumb value ${state}`}
                slotProps={{
                    track: { className: priceSliderStyles.track },
                    thumb: { className: priceSliderStyles.thumb },
                    rail: { className: priceSliderStyles.rail },
                }}
                disabled={lowestFilterPrice === highestFilterPrice}
                onChange={(e, value) => {
                    e.preventDefault()
                    if (value && Array.isArray(value)) {
                        setDisplayedMinMaxValue([value[0], value[1]])
                    }
                }}
                onChangeCommitted={(e) => {
                    e.preventDefault()
                    if (detailsState.listing) {
                        toggleDetails({ listing: null })
                    }
                    if (displayedMinMaxValue && Array.isArray(displayedMinMaxValue)) {
                        if (timeoutRef.current) clearTimeout(timeoutRef.current)
                        if (displayedMinMaxValue[0] !== priceFilterState.currentMin) {
                            onFilterChange({
                                field: 'currentMin',
                                value: displayedMinMaxValue[0],
                                shouldTrackFilterChange: true,
                            })
                            timeoutRef.current = setTimeout(() => {
                                trackEvent('FilterUse', 'UsedTicketFilters', 'PriceLow', displayedMinMaxValue[0], false)
                            }, TRACKING_TIMEOUT) as unknown as number
                        }
                        if (displayedMinMaxValue[1] !== priceFilterState.currentMax) {
                            onFilterChange({
                                field: 'currentMax',
                                value: displayedMinMaxValue[1],
                                shouldTrackFilterChange: true,
                            })
                            timeoutRef.current = setTimeout(() => {
                                trackEvent(
                                    'FilterUse',
                                    'UsedTicketFilters',
                                    'PriceHigh',
                                    displayedMinMaxValue[1],
                                    false,
                                )
                            }, TRACKING_TIMEOUT) as unknown as number
                        }
                    }
                }}
            />
        </div>
    )

    const minInput = (
        <div className={currencyInput}>
            <Input
                data-testid="priceMin"
                onBlur={onLowerBlur}
                type="number"
                className={inputStyle}
                sizing="lg"
                value={displayedMinMaxValue[0]}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (detailsState.listing) {
                        toggleDetails({ listing: null })
                    }
                    if (e.target.value === '') {
                        onFilterChange({
                            field: 'currentMin',
                            value: Number.POSITIVE_INFINITY,
                            shouldTrackFilterChange: false,
                        })
                    } else {
                        const value = parseInt(e.target.value)
                        if (!isNaN(value)) {
                            onFilterChange({ field: 'currentMin', value, shouldTrackFilterChange: true })
                            trackEvent('FilterUse', 'UsedTicketFilters', 'PriceLow', value, false)
                        }
                    }
                }}
            />
            <i className={iconStyle}>{currencySymbol}</i>
        </div>
    )

    const maxInput = (
        <div className={currencyInput}>
            <Input
                data-testid="priceMax"
                onBlur={onUpperBlur}
                type="text"
                className={inputStyle}
                sizing="lg"
                value={`${displayedMinMaxValue[1]}${
                    displayedMinMaxValue[1] !== priceRangeObject.highestPrice &&
                    displayedMinMaxValue[1] !== priceRangeObject.highestAllInPrice &&
                    displayedMinMaxValue[1] === highestPriceRef.current
                        ? '+'
                        : ''
                }`}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (detailsState.listing) {
                        toggleDetails({ listing: null })
                    }
                    if (e.target.value === '' || e.target.value === '+') {
                        onFilterChange({
                            field: 'currentMax',
                            value: Number.NEGATIVE_INFINITY,
                            shouldTrackFilterChange: false,
                        })
                    } else {
                        const value = parseInt(stripPlus(e.target.value))
                        if (!isNaN(value)) {
                            onFilterChange({
                                field: 'currentMax',
                                value,
                                shouldTrackFilterChange: true,
                            })
                            trackEvent('FilterUse', 'UsedTicketFilters', 'PriceHigh', value, false)
                        }
                    }
                }}
            />
            <i className={iconStyle}>{currencySymbol}</i>
        </div>
    )

    return (
        <>
            <Row className={sliderRow}>
                {singleRow && showSlider && (
                    <>
                        {minInput}
                        {sliderElement}
                        {maxInput}
                    </>
                )}

                {!singleRow && (
                    <>
                        {minInput}
                        <span className={styles.inputDivider}>&#8722;</span>
                        {maxInput}
                    </>
                )}
            </Row>
            {!singleRow && sliderElement}
        </>
    )
}

export default Slider
